'use client'

// Os quatro caminhos do link da bio, no visual de /entrar e /atendimento24h
// (fundo, logo, ficha 24h, selo dourado, CTA dourado e verde do WhatsApp).
//
//   Quero começar a jogar  -> /entrar, com a origem "bio do Instagram"
//   Já sou jogador         -> /ja-sou-jogador, que abre o WhatsApp da casa com
//                             a frase do "Já sou jogador" (o número não fica
//                             escrito aqui, mora na rota)
//   Área do Jogador        -> app.clubesbrazil.com
//   Site                   -> www.clubesbrazil.com
//
// Cada toque vira evento no GTM com o destino, para saber quantos da bio vão
// para cada caminho.

import { useEffect, useState } from 'react'
import AvisoMaioridade from '@/components/layout/AvisoMaioridade'
import { gtmEvent } from '@/lib/gtm'
import { lerGrupoGuardado, nomeDoLembrete, type GrupoGuardado } from '@/lib/grupo-guardado'

// Imagens por /publico, que a landing repassa para o admin: a página é
// servida em www.clubesbrazil.com e o endereço do admin não aparece no HTML.
const asset = (p: string) => `/publico${p}`
const LOGO_URL = asset('/logo-horizontal.webp')
const HUB_URL = asset('/atendimento-24h.webp')
const BG_MOBILE = asset('/login-bg-mobile.webp')
const BG_DESKTOP = asset('/login-bg-desktop.webp')

// Pelo /jogar, como os botões do site: ele confere estoque e Z-API antes de
// mandar ao formulário e, se não der, leva para a gestora em vez de um erro
// (estudo do funil de 13/10/2026). O /jogar repassa a query ao destino.
const LINK_NOVO = '/jogar?origem=bio&utm_source=instagram&utm_medium=bio&utm_campaign=links_bio'
const LINK_JA_SOU = '/ja-sou-jogador?origem=bio'
const LINK_AREA = 'https://app.clubesbrazil.com'
const LINK_SITE = 'https://www.clubesbrazil.com'

function Seta() {
  return (
    <svg className="seta" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function IconeWhatsapp() {
  return (
    <svg className="icone" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

// Cada botão leva um ícone do lugar para onde vai, todos na mesma coluna à
// esquerda: o olho desce pelos ícones e os textos começam alinhados.
// Duas cartas, a mão que o jogador recebe ao sentar. Ecoa as cartas da logo.
function IconeCartas() {
  return (
    <svg className="icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5.5" width="10" height="14" rx="1.8" transform="rotate(-14 8 12.5)" />
      <rect x="10" y="3.5" width="10" height="14" rx="1.8" transform="rotate(12 15 10.5)" fill="currentColor" />
    </svg>
  )
}

function IconeJogador() {
  return (
    <svg className="icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </svg>
  )
}

function IconeSite() {
  return (
    <svg className="icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
    </svg>
  )
}

export default function LinksBio() {
  const clique = (destino: string) => () => gtmEvent('links_bio_clique', { destino })
  // Quem pegou grupo pelo formulário neste aparelho e voltou pela bio vê o
  // grupo de novo (F2-01, 13/10/2026). A casa não chama primeiro: esta é a
  // recuperação de quem pegou o grupo e não entrou. Lido depois da montagem
  // para o HTML do servidor não depender do aparelho.
  const [grupo, setGrupo] = useState<GrupoGuardado | null>(null)
  useEffect(() => {
    const g = lerGrupoGuardado()
    setGrupo(g)
    if (g) gtmEvent('links_bio_lembrete', {})
  }, [])
  const nome = grupo ? nomeDoLembrete(grupo.nome) : ''
  // Marca do toque (12/10/2026): o /ja-sou-jogador só conta clique que traz
  // ?t= de agora. Robô que segue o link cru é redirecionado, mas não conta.
  const marcarToque = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.href = `${LINK_JA_SOU}&t=${Date.now().toString(36)}`
  }

  return (
    <main className="relative flex min-h-[100dvh] flex-col items-center overflow-y-auto bg-[#050A13] px-6 pb-8 pt-6 text-center text-[var(--cb-text)]">
      <img src={BG_MOBILE} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-30 lg:hidden" draggable={false} />
      <img src={BG_DESKTOP} alt="" aria-hidden className="absolute inset-0 hidden h-full w-full object-cover opacity-[0.42] lg:block" draggable={false} />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 80% at 18% 55%, rgba(62,143,78,0.25) 0%, rgba(0,0,0,0) 60%), radial-gradient(50% 60% at 92% 8%, rgba(251,191,36,0.16) 0%, rgba(0,0,0,0) 60%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(rgba(5,10,19,0.78) 0%, rgba(5,10,19,0.65) 25%, rgba(5,10,19,0.78) 60%, rgba(5,10,19,0.88) 100%)',
        }}
      />

      <img src={LOGO_URL} alt="Clubes Brazil - Poker Online" className="relative z-10 w-full max-w-[240px] select-none lg:max-w-[300px]" draggable={false} />

      <section className="card-lg relative z-10 mt-4 flex w-full max-w-[440px] flex-col items-center lg:mt-6 lg:rounded-[32px] lg:p-10">
        <div className="hub mb-2" aria-hidden>
          <img src={HUB_URL} alt="" className="h-auto w-[78px] select-none lg:w-[94px]" draggable={false} />
        </div>

        <div className="flex items-center gap-2">
          <span className="ping" aria-hidden />
          <span className="text-[12px] font-medium text-[var(--cb-gold)]">Atendimento 24h</span>
        </div>

        <h1
          className="mt-2.5 text-[26px] font-bold leading-[1.13] tracking-[-0.02em] [text-wrap:balance]"
          style={{ fontFamily: 'var(--font-sora), system-ui, sans-serif' }}
        >
          Mais de <span className="text-[var(--cb-gold)]">3.000 jogadores</span> já estão com a gente
        </h1>

        {grupo ? (
          <div className="lembrete mt-6 w-full" style={{ fontFamily: 'var(--font-sora), system-ui, sans-serif' }}>
            <p className="text-[15px] font-semibold leading-snug text-[var(--cb-text)]">
              {nome ? `Seu grupo continua lá, ${nome}` : 'Seu grupo continua lá'}
            </p>
            <a
              href={grupo.link}
              onClick={clique('meu_grupo')}
              className="opcao-link cta cta-whats mt-3"
            >
              <IconeWhatsapp />
              Entrar no meu grupo
              <Seta />
            </a>
          </div>
        ) : null}

        <nav className="mt-6 flex w-full flex-col gap-3" aria-label="Por onde entrar" style={{ fontFamily: 'var(--font-sora), system-ui, sans-serif' }}>
          <a href={LINK_NOVO} onClick={clique('jogador_novo')} className="opcao-link cta">
            <IconeCartas />
            Quero começar a jogar
            <Seta />
          </a>
          <a
            href={LINK_JA_SOU}
            onClick={(e) => {
              marcarToque(e)
              clique('ja_sou_jogador')()
            }}
            onAuxClick={marcarToque}
            className="opcao-link cta cta-whats"
          >
            <IconeWhatsapp />
            Já sou jogador
            <Seta />
          </a>
          <a href={LINK_AREA} onClick={clique('area_jogador')} className="opcao-link opcao-sec">
            <IconeJogador />
            Entrar na Área do Jogador
            <Seta />
          </a>
          <a href={LINK_SITE} onClick={clique('site')} className="opcao-link opcao-sec">
            <IconeSite />
            Conhecer o Clubes Brazil
            <Seta />
          </a>
        </nav>
      </section>

      <p className="relative z-10 mt-5 flex flex-wrap items-center justify-center gap-2 text-center text-[11px] text-[var(--cb-text-faint)]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
          <rect x="4" y="10.5" width="16" height="10" rx="2.2" />
          <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
        </svg>
        <span>Ambiente seguro</span>
        <span aria-hidden className="opacity-50">•</span>
        <span>Atendimento 24h</span>
      </p>
      <AvisoMaioridade />

      <style jsx>{`
        .lembrete {
          padding: 14px;
          border-radius: 16px;
          border: 1px solid rgba(214, 169, 60, 0.35);
          background: rgba(214, 169, 60, 0.07);
        }
        .ping {
          position: relative;
          display: inline-block;
          height: 8px;
          width: 8px;
          border-radius: 9999px;
          background: var(--cb-gold);
          flex: none;
        }
        .ping::after {
          content: '';
          position: absolute;
          inset: -5px;
          border-radius: 9999px;
          border: 1px solid var(--cb-gold);
          opacity: 0.6;
          animation: ping-anim 1.8s ease-out infinite;
        }
        @keyframes ping-anim {
          0% {
            transform: scale(0.6);
            opacity: 0.7;
          }
          100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }
        .hub {
          filter: drop-shadow(0 14px 26px rgba(0, 0, 0, 0.5));
          animation: hub-bounce 2.6s ease-in-out infinite;
        }
        .hub img {
          animation: hub-spin 10s linear infinite;
        }
        @keyframes hub-bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes hub-spin {
          from {
            transform: rotate(0);
          }
          to {
            transform: rotate(360deg);
          }
        }
        /* Os quatro botões com a mesma forma e altura: o olho compara as
           opções sem ler. A cor diz o peso: dourado para quem chega, verde do
           WhatsApp para quem já joga, contorno para o resto. */
        .opcao-link {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          min-height: 56px;
          padding: 0 18px;
          border-radius: 13px;
          font-size: 15.5px;
          font-weight: 700;
          text-decoration: none;
          text-align: left;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.12s ease, filter 0.15s ease, background-color 0.15s ease;
        }
        .opcao-link:active {
          transform: translateY(1px) scale(0.99);
        }
        /* CTA dourado oficial da marca, igual ao de /entrar e /atendimento24h. */
        .cta {
          color: #0d3b14;
          border: 1px solid #9a7410;
          background: linear-gradient(180deg, #f5c842, #d4a017);
        }
        .cta-whats {
          border-color: #13803f;
          background: linear-gradient(180deg, #3ddc84, #1faa59);
          color: #04220f;
        }
        .cta:hover {
          filter: brightness(1.05);
        }
        .opcao-sec {
          color: var(--cb-text);
          border: 1px solid var(--cb-border);
          background: rgba(7, 12, 24, 0.6);
        }
        .opcao-sec:hover {
          background: var(--cb-surface-top);
        }
        .opcao-link :global(.seta) {
          margin-left: auto;
          width: 18px;
          height: 18px;
          flex: none;
          opacity: 0.85;
        }
        .opcao-link :global(.icone) {
          width: 20px;
          height: 20px;
          flex: none;
        }
        /* Igual ao /entrar: no celular não há card, tudo fica sobre a imagem.
           O card só aparece no desktop. */
        @media (min-width: 1024px) {
          .card-lg {
            background-color: rgba(14, 24, 42, 0.85);
            background-image: radial-gradient(circle at 50% 0px, rgba(79, 168, 97, 0.1), rgba(0, 0, 0, 0) 38%),
              radial-gradient(circle at 50% 6%, rgba(250, 204, 21, 0.06), rgba(0, 0, 0, 0) 30%),
              linear-gradient(rgba(18, 29, 51, 0.94), rgba(12, 20, 36, 0.94) 55%, rgba(8, 14, 26, 0.96));
            box-shadow: 0 28px 70px -22px rgba(0, 0, 0, 0.85), 0 48px 110px -32px rgba(62, 143, 78, 0.22),
              0 0 0 1px rgba(62, 143, 78, 0.18), inset 0 1px 0 0 rgba(255, 255, 255, 0.08),
              inset 0 -1px 0 0 rgba(255, 255, 255, 0.03);
            backdrop-filter: blur(40px) saturate(1.5);
            -webkit-backdrop-filter: blur(40px) saturate(1.5);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .ping::after,
          .hub,
          .hub img {
            animation: none !important;
          }
        }
      `}</style>
    </main>
  )
}
